import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User as SupabaseUser, Session } from "@supabase/supabase-js";

export interface AppUser {
  id: string;
  name: string;
  email: string;
}

interface AuthContextType {
  user: AppUser | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  register: (name: string, email: string, password: string, cpf?: string) => Promise<{ success: boolean; error?: string }>;
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  updatePassword: (password: string) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function translateAuthError(message: string): string {
  const map: Record<string, string> = {
    "Invalid login credentials": "Email ou senha inválidos",
    "Email not confirmed": "Email ainda não confirmado. Verifique sua caixa de entrada.",
    "email rate limit exceeded": "Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.",
    "over_email_send_rate_limit": "Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.",
    "For security purposes, you can only request this once every 60 seconds": "Por segurança, aguarde 60 segundos antes de tentar novamente.",
    "User already registered": "Este email já está cadastrado",
    "Password should be at least 6 characters": "A senha deve conter pelo menos 8 caracteres, incluindo letras maiúsculas, minúsculas e números.",
    "New password should be different from the old password": "A nova senha deve ser diferente da senha atual.",
    "Auth session missing!": "Sessão expirada. Faça login novamente.",
    "Invalid Refresh Token: Refresh Token Not Found": "Sessão expirada. Faça login novamente.",
  };
  for (const [key, val] of Object.entries(map)) {
    if (message.includes(key)) return val;
  }
  return message;
}

function mapSupabaseUser(su: SupabaseUser): AppUser {
  return {
    id: su.id,
    name: su.user_metadata?.name || (() => {
      const prefix = su.email?.split("@")[0] || "";
      const first = prefix.split(".")[0];
      return first.charAt(0).toUpperCase() + first.slice(1);
    })(),
    email: su.email || "",
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Set up auth listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ? mapSupabaseUser(newSession.user) : null);
      setIsLoading(false);

      // Auto-redirect on email confirmation or password recovery
      if (event === "SIGNED_IN" && newSession) {
        // Check if this came from an email confirmation (hash tokens in URL)
        const hash = window.location.hash;
        if (hash && (hash.includes("access_token") || hash.includes("type=signup") || hash.includes("type=magiclink"))) {
          // Clean the hash and redirect to home
          window.location.hash = "";
          window.location.replace("/");
        }
      }
      if (event === "PASSWORD_RECOVERY") {
        window.location.replace("/redefinir-senha");
      }
    });

    // THEN check existing session
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      setSession(existingSession);
      setUser(existingSession?.user ? mapSupabaseUser(existingSession.user) : null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      return { success: false, error: translateAuthError(error.message) };
    }
    return { success: true };
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
  }, []);

  const register = useCallback(async (name: string, email: string, password: string, cpf?: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { name },
        emailRedirectTo: window.location.origin,
      },
    });
    if (error) {
      return { success: false, error: translateAuthError(error.message) };
    }
    // Save CPF to public.users if provided
    if (cpf && data.user) {
      await supabase.from("users").update({ cpf }).eq("id", data.user.id);
    }
    return { success: true };
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/redefinir-senha`,
    });
    if (error) return { success: false, error: translateAuthError(error.message) };
    return { success: true };
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) return { success: false, error: translateAuthError(error.message) };
    return { success: true };
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      session,
      isAuthenticated: !!session,
      isLoading,
      login,
      logout,
      register,
      resetPassword,
      updatePassword,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
